import { useNotificationToastApi } from "@/hooks/useNotificationToastApi";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { useModalManager } from "./modalManager";
import { useNavigatorApi, useNavigatorDriver } from "./navigator";
import { usePermissions } from "@/hooks/usePermissions";
import { useNav } from "@/chat2/Chat2Provider";
import { useTabs } from "@/contexts/tabsContext";
import {
  type Workspace,
  type CreateWorkspace,
  type CreateWorkspaceErrorCodes,
  type CloneWorkspaceOptions,
  type IWorkspaceLibraryService,
  WORKSPACE_LIBRARY_SERVICE_IDENT,
} from "@bloomstack/apis/workspace";
import { WORKSPACE_MESSAGES_SEND } from "@bloomstack/apis/entityPermissions";
import { CAN_DELETE_WORKSPACES } from "@bloomstack/apis/permissions";
import { useUser } from "@/hooks/useUser";
import { useMobile } from "@/components/responsive";
import { useService } from "@/hooks/useService";
import { AlertDialog } from "@/components/ui/alertDialog";
import { RPC_TIER_GLOBAL } from "@/constants/rpcPriorities";
import type { Result } from "@bloomstack/greenhouse/results";
import Dynamic from '@/lib/dynamic';
import ComponentSpinner from "@/components/ui/componentSpinner";

import { Dialog2 } from "@/components/ui/dialog2";

const WorkspaceBrowser = Dynamic(() => import("@/components/workspace/WorkspaceBrowser").then(mod => ({ default: mod.WorkspaceBrowser })), { ssr: false, loading: () => <ComponentSpinner /> });

// ── Shared defaults ────────────────────────────────────────────

export const WORKSPACE_DEFAULTS: Partial<Workspace> = {
  memoryBrainModelId: "",
  enableMemoryBrain: false,
  allowFailure: false,
  ponderTimelineBudget: 4000,
  prompterBudgetPct: 20,
  ponderSearchDepth: 1,
  ponderMaxRounds: 1,
  ponderMaxMaterialDocs: 20,
  ponderRelevanceThreshold: 0.1,
  summarizerStrategy: "max-context",
  summarizerTokenThreshold: 70,
};

// ── Context type ───────────────────────────────────────────────

export type WorkspaceLibContextType = {
  openWorkspaceLibrary: (id?: string) => void;
  openWorkspace: (workspace: Workspace) => void;
  cloneWorkspace: (id: string, overrides?: Partial<CreateWorkspace>, cloneOptions?: CloneWorkspaceOptions) => Promise<Result<Workspace, CreateWorkspaceErrorCodes>>;
  deleteWorkspace: (workspace: Workspace) => void;
  createWorkspace: () => void;
};

export const WorkspaceLibContext = createContext<WorkspaceLibContextType | undefined>(undefined);

export const useWorkspaceLibrary = () => {
  const context = useContext(WorkspaceLibContext);
  if (!context) {
    throw new Error("useWorkspaceLibrary must be used within a WorkspaceLibraryProvider");
  }
  return context;
};

export type WorkspaceLibraryProviderProps = {
  children?: React.ReactNode;
};

export const WorkspaceLibraryProvider: React.FC<WorkspaceLibraryProviderProps> = ({ children }) => {
  const [user] = useUser();
  const perms = usePermissions();
  const modal = useModalManager();
  const nav = useNavigatorApi();
  const tabs = useTabs(false);
  const navCtx = useNav(false);
  const notifications = useNotificationToastApi();
  const isMobile = useMobile();
  const workspaceLibService = useService<IWorkspaceLibraryService>(WORKSPACE_LIBRARY_SERVICE_IDENT)
    .withPriority('normal', () => RPC_TIER_GLOBAL);
  const modalCloseRef = useRef<(() => void) | null>(null);

  // ── Open workspace in tabs ─────────────────────────────────

  const openWorkspace = useCallback((workspace: Workspace) => {
    const isOwner = workspace.ownerId === user?.id;
    const hasWorkspaceAccess = perms.has(WORKSPACE_MESSAGES_SEND, workspace.id);

    if (!isOwner && !hasWorkspaceAccess) {
      notifications.showNotification({
        title: "Error",
        message: "You do not have permission to access this workspace"
      });
      return;
    }

    // publicClonable workspaces are clone-only for non-owners and non-members
    if (workspace.publicClonable && !isOwner && !hasWorkspaceAccess) {
      notifications.showNotification({
        title: "Clone Only",
        message: "This workspace is available for cloning. Clone it to create your own copy."
      });
      return;
    }

    if (tabs) {
      tabs.open('workspace', workspace.id);
    }

    if (isMobile) {
      navCtx?.show(false);
    }
  }, [tabs, navCtx, perms, user, isMobile, notifications]);

  // ── Clone workspace ────────────────────────────────────────

  const cloneWorkspace = useCallback(async (
    id: string,
    overrides?: Partial<CreateWorkspace>,
    cloneOptions?: CloneWorkspaceOptions,
  ): Promise<Result<Workspace, CreateWorkspaceErrorCodes>> => {
    const result = await workspaceLibService.clone(id, {
      ...overrides,
    }, cloneOptions);

    if (!result.success) {
      notifications.showNotification({
        title: result.error?.code || "Error",
        message: result.error?.reason || "Failed to clone workspace",
        status: "critical",
      });
    }

    return result;
  }, [workspaceLibService, notifications]);

  // ── Delete workspace (with confirmation) ───────────────────

  const deleteWorkspace = useCallback((workspace: Workspace) => {
    if (!perms.has(CAN_DELETE_WORKSPACES)) {
      notifications.showNotification({
        title: "Error",
        message: "You do not have permission to delete this workspace",
      });
      return;
    }

    modal.show((api) => (
      <AlertDialog
        intent="danger"
        title="Delete Workspace"
        message={
          <>
            <p>Are you sure you want to delete this workspace?</p>
            <p>This action cannot be undone!</p>
          </>
        }
        onConfirm={async () => {
          const result = await workspaceLibService.delete(workspace.id);
          if (!result.success) {
            notifications.showNotification({
              title: "Error",
              message: result.error?.reason || "An error occurred while deleting the workspace",
            });
          }
        }}
        onClose={() => api.close()}
      />
    ));
  }, [perms, notifications, modal, workspaceLibService]);

  // ── Open workspace library modal ───────────────────────────

  const handleOpenWorkspaceLibrary = useCallback((id?: string, mode?: "view" | "edit") => {
    modal.show((api) => {
      modalCloseRef.current = () => api.close();
      return <Dialog2
        onClose={() => api.close()}
        className="w-[100dvw] h-[100dvh] rounded-none border-0 shadow-none md:h-[90dvh] md:w-[90dvw] md:max-w-6xl md:rounded-md md:border md:shadow-md"
        contentClassName="!gap-0"
        actionsClassName="absolute top-3 right-3 z-20 md:relative md:top-auto md:right-auto md:z-auto md:p-2"
      >
        <WorkspaceBrowser
          initialWorkspaceId={id}
          initialMode={mode}
          onClose={() => api.close()}
          onOpen={(workspace: Workspace) => {
            api.close();
            openWorkspace(workspace);
          }}
          cloneWorkspace={cloneWorkspace}
          deleteWorkspace={deleteWorkspace}
        />
      </Dialog2>;
    });
  }, [modal, openWorkspace, cloneWorkspace, deleteWorkspace]);

  // ── Create workspace (opens modal in create mode) ──────────

  const handleCreateWorkspace = useCallback(() => {
    modal.show((api) => {
      modalCloseRef.current = () => api.close();
      return <Dialog2
        onClose={() => api.close()}
        className="w-[100dvw] h-[100dvh] rounded-none border-0 shadow-none md:h-[90dvh] md:w-[90dvw] md:max-w-6xl md:rounded-md md:border md:shadow-md"
        contentClassName="!gap-0"
        actionsClassName="absolute top-3 right-3 z-20 md:relative md:top-auto md:right-auto md:z-auto md:p-2"
      >
        <WorkspaceBrowser
          onClose={() => api.close()}
          onOpen={(workspace: Workspace) => {
            api.close();
            openWorkspace(workspace);
          }}
          cloneWorkspace={cloneWorkspace}
          deleteWorkspace={deleteWorkspace}
        />
      </Dialog2>;
    });
  }, [modal, openWorkspace, cloneWorkspace, deleteWorkspace]);

  // ── Context API ────────────────────────────────────────────

  const api = useMemo(() => ({
    openWorkspaceLibrary: handleOpenWorkspaceLibrary,
    openWorkspace,
    cloneWorkspace,
    deleteWorkspace,
    createWorkspace: handleCreateWorkspace,
  }), [handleOpenWorkspaceLibrary, openWorkspace, cloneWorkspace, deleteWorkspace, handleCreateWorkspace]);

  // ── Navigation driver ──────────────────────────────────────

  useNavigatorDriver(async (route) => {
    switch(route.route) {
      case "workspace":
        nav.clearRoute();
        openWorkspace(route.props.workspace);
        break;
      case "workspace-edit":
        nav.clearRoute();
        if (route.props.workspaceId) {
          handleOpenWorkspaceLibrary(route.props.workspaceId, "edit");
        }
        break;
      case "workspace-create":
        nav.clearRoute();
        handleCreateWorkspace();
        break;
      case "workspace-library":
        nav.clearRoute();
        handleOpenWorkspaceLibrary();
        break;
      case "workspace-view":
        nav.clearRoute();
        if (route.props.workspaceId) {
          handleOpenWorkspaceLibrary(route.props.workspaceId);
        }
        break;
      case "workspace-clone":
        nav.clearRoute();
        if (route.props.workspaceId) {
          const cloned = await cloneWorkspace(route.props.workspaceId);
          if (cloned.success) {
            handleOpenWorkspaceLibrary(cloned.result.id);
          }
        }
        break;
    }
  });

  return (
    <WorkspaceLibContext.Provider value={api}>
      {children}
    </WorkspaceLibContext.Provider>
  );
};
